import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { getTeacherWorkshops, updateWorkshopRecording } from '@/lib/db'
import type { UnitWorkshop } from '@/types'
import {
  PlayCircle,
  Video,
  Calendar,
  Clock,
  Loader2,
  CheckCircle,
  X,
  Link as LinkIcon,
  Save,
  ExternalLink
} from 'lucide-react'
import { cn } from '@/lib/utils'

/** Convierte una URL de Google Drive (share o directa) a URL de embed */
function toEmbedUrl(url: string): string | null {
  const match = url.match(/drive\.google\.com\/file\/d\/([^/]+)/)
  if (match) return `https://drive.google.com/file/d/${match[1]}/preview`
  if (url.includes('/preview')) return url
  return null
}

export default function TeacherRecordedWorkshops() {
  const { teacher } = useAuth()
  const [workshops, setWorkshops] = useState<UnitWorkshop[]>([])
  const [loading, setLoading] = useState(true)
  const [linkTarget, setLinkTarget] = useState<UnitWorkshop | null>(null)
  const [videoUrl, setVideoUrl] = useState('')
  const [urlError, setUrlError] = useState('')
  const [savingUrl, setSavingUrl] = useState(false)
  const [previewVideo, setPreviewVideo] = useState<UnitWorkshop | null>(null)

  useEffect(() => {
    if (!teacher?.id) return
    loadWorkshops()
  }, [teacher?.id])

  const loadWorkshops = async () => {
    setLoading(true)
    const { data } = await getTeacherWorkshops(teacher!.id)
    if (data) {
      const today = new Date().toISOString()
      const completed = data.filter(w => w.scheduled_date && w.scheduled_date < today)
      setWorkshops(completed)
    }
    setLoading(false)
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  const openLinkModal = (workshop: UnitWorkshop) => {
    setLinkTarget(workshop)
    setVideoUrl(workshop.recording_url || '')
    setUrlError('')
  }

  const handleSaveUrl = async () => {
    if (!linkTarget) return
    setUrlError('')

    const trimmed = videoUrl.trim()
    if (!trimmed) {
      setUrlError('Ingresa un enlace de Google Drive')
      return
    }

    const embed = toEmbedUrl(trimmed)
    if (!embed) {
      setUrlError('El enlace no parece ser de Google Drive. Usa el formato: https://drive.google.com/file/d/.../view')
      return
    }

    setSavingUrl(true)
    try {
      const { error } = await updateWorkshopRecording(linkTarget.id, trimmed)
      if (error) throw new Error('Error al guardar el enlace')

      setWorkshops(prev =>
        prev.map(w => w.id === linkTarget.id ? { ...w, recording_url: trimmed } : w)
      )
      setLinkTarget(null)
    } catch (err: any) {
      setUrlError(err.message || 'Error al guardar')
    } finally {
      setSavingUrl(false)
    }
  }

  const previewEmbedUrl = previewVideo?.recording_url ? toEmbedUrl(previewVideo.recording_url) : null

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <PlayCircle className="w-7 h-7 text-primary" />
          Talleres grabados
        </h1>
        <p className="text-muted-foreground mt-1">
          Agrega enlaces de Google Drive para que los alumnos puedan ver las grabaciones
        </p>
      </div>

      {/* Lista */}
      {workshops.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border">
          <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">Sin talleres completados</p>
          <p className="text-muted-foreground">
            Cuando completes un taller en vivo, podrás agregar el enlace de la grabación aquí
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {workshops.map((w) => {
            const unitName = (w as any).unit?.title_es || (w as any).unit?.title || ''
            const cohortName = (w as any).cohort?.name || ''
            const titleParts = [unitName, cohortName].filter(Boolean).join(' — ')
            const title = titleParts ? `Taller grabado - ${titleParts}` : 'Taller grabado'
            const hasRecording = !!w.recording_url

            return (
              <div
                key={w.id}
                className="bg-card rounded-xl border p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className={cn(
                    'w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0',
                    hasRecording ? 'bg-green-100' : 'bg-gray-100'
                  )}>
                    {hasRecording ? (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    ) : (
                      <Video className="w-8 h-8 text-gray-400" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold">{title}</h3>
                      </div>
                      <span className={cn(
                        'px-2 py-1 rounded-full text-xs font-medium',
                        hasRecording
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      )}>
                        {hasRecording ? 'Enlace agregado' : 'Sin enlace'}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                      {w.scheduled_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(w.scheduled_date)}
                        </span>
                      )}
                      {w.duration_minutes && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {w.duration_minutes} min
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {hasRecording ? (
                      <>
                        <button
                          onClick={() => setPreviewVideo(w)}
                          className="inline-flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                          <PlayCircle className="w-4 h-4" />
                          Ver grabación
                        </button>
                        <button
                          onClick={() => openLinkModal(w)}
                          className="inline-flex items-center gap-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                        >
                          <LinkIcon className="w-4 h-4" />
                          Cambiar enlace
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => openLinkModal(w)}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                      >
                        <LinkIcon className="w-4 h-4" />
                        Agregar enlace
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Modal: Agregar/Cambiar enlace */}
      {linkTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {linkTarget.recording_url ? 'Cambiar enlace' : 'Agregar enlace'}
              </h3>
              <button
                onClick={() => setLinkTarget(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                disabled={savingUrl}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Taller: <strong>{(() => { const u = (linkTarget as any).unit?.title_es || (linkTarget as any).unit?.title || ''; const c = (linkTarget as any).cohort?.name || ''; const parts = [u, c].filter(Boolean).join(' — '); return parts ? `Taller grabado - ${parts}` : 'Taller grabado' })()}</strong>
            </p>

            <div className="space-y-3">
              <div>
                <label htmlFor="recording-url" className="block text-sm font-medium mb-1">Enlace de Google Drive</label>
                <input
                  type="url"
                  id="recording-url"
                  value={videoUrl}
                  onChange={(e) => { setVideoUrl(e.target.value); setUrlError('') }}
                  placeholder="https://drive.google.com/file/d/.../view?usp=sharing"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400"
                />
              </div>

              <p className="text-xs text-muted-foreground">
                Sube el video a Google Drive, compártelo como público y pega el enlace aquí.
              </p>

              {urlError && (
                <p className="text-sm text-red-600">{urlError}</p>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setLinkTarget(null)}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                disabled={savingUrl}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveUrl}
                disabled={savingUrl}
                className="flex-1 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {savingUrl ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Guardar enlace
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Preview video */}
      {previewVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="relative w-full max-w-4xl">
            <button
              onClick={() => setPreviewVideo(null)}
              className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="bg-black rounded-xl overflow-hidden">
              {previewEmbedUrl ? (
                <iframe
                  src={previewEmbedUrl}
                  className="w-full aspect-video"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                />
              ) : (
                <div className="w-full aspect-video flex items-center justify-center bg-gray-900 text-gray-400">
                  Enlace no válido
                </div>
              )}
              <div className="p-4 bg-gray-900 text-white flex items-center justify-between">
                <h3 className="font-semibold">
                  {(() => { const u = (previewVideo as any).unit?.title_es || (previewVideo as any).unit?.title || ''; const c = (previewVideo as any).cohort?.name || ''; const parts = [u, c].filter(Boolean).join(' — '); return parts ? `Taller grabado - ${parts}` : 'Taller grabado' })()}
                </h3>
                {previewVideo.recording_url && (
                  <a
                    href={previewVideo.recording_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-400 hover:underline"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Abrir en Drive
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
